import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import {
    PlusIcon,
    PencilIcon,
    TrashIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import { specializationAPI, degreeAPI } from '../../services/api';
import PermissionWrapper from '../../components/Admin/PermissionWrapper';

const AdminSpecializations = () => {
    return (
        <PermissionWrapper requiredPermission="courses">
            <AdminSpecializationsContent />
        </PermissionWrapper>
    )
}

const AdminSpecializationsContent = () => {
    const [filters, setFilters] = useState({
        search: '',
        degree: '',
        isActive: '',
        page: 1,
        limit: 10,
        sort: 'name'
    });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSpecialization, setEditingSpecialization] = useState(null);
    const queryClient = useQueryClient();

    const { register, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm();

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['admin-specializations', filters],
        queryFn: () => specializationAPI.getAdminSpecializations(filters),
        keepPreviousData: true,
    });

    const { data: degreesData } = useQuery({
        queryKey: ['degrees'],
        queryFn: () => degreeAPI.getDegrees(),
    });

    const createMutation = useMutation({
        mutationFn: specializationAPI.createSpecialization,
        onSuccess: () => {
            toast.success('Specialization created successfully!');
            queryClient.invalidateQueries(['admin-specializations']);
            handleCloseModal();
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to create specialization');
        }
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => specializationAPI.updateSpecialization(id, data),
        onSuccess: () => {
            toast.success('Specialization updated successfully!');
            queryClient.invalidateQueries(['admin-specializations']);
            handleCloseModal();
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to update specialization');
        }
    });

    const deleteMutation = useMutation({
        mutationFn: specializationAPI.deleteSpecialization,
        onSuccess: () => {
            toast.success('Specialization deleted successfully!');
            queryClient.invalidateQueries(['admin-specializations']);
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to delete specialization');
        }
    });

    const handleFilterChange = (key, value) => {
        console.log('Filter change:', key, value); // Debug log
        setFilters(prev => ({
            ...prev,
            [key]: value,
            page: key === 'page' ? value : 1
        }));
    };

    const handleEdit = (specialization) => {
        setEditingSpecialization(specialization);
        reset({
            name: specialization.name,
            description: specialization.description,
            degree: specialization.degree?._id || specialization.degree,
            isActive: specialization.isActive,
            order: specialization.order
        });
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this specialization?')) {
            deleteMutation.mutate(id);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingSpecialization(null);
        reset();
    };

    const onSubmit = (data) => {
        if (editingSpecialization) {
            updateMutation.mutate({ id: editingSpecialization._id, data });
        } else {
            createMutation.mutate(data);
        }
    };

    const specializations = data?.data?.specializations || [];
    const pagination = data?.data?.pagination || {};

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <p className="text-red-600 mb-4">Error loading specializations</p>
                    <button onClick={refetch} className="btn-primary">
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>Manage Specializations | Margdarshak Admin</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <div className="min-h-screen bg-gray-50">
                {/* Quick Actions */}
                <div className="bg-white shadow-sm border-b border-gray-200">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
                        <div className="flex justify-end">
                            <button
                                onClick={() => setIsModalOpen(true)}
                                className="btn-primary"
                            >
                                <PlusIcon className="w-5 h-5 mr-2" />
                                Add Specialization
                            </button>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white shadow-sm border-b border-gray-200">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="relative">
                                <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search specializations..."
                                    value={filters.search}
                                    onChange={(e) => handleFilterChange('search', e.target.value)}
                                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 w-full"
                                />
                            </div>
                            <select
                                value={filters.degree}
                                onChange={(e) => handleFilterChange('degree', e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                            >
                                <option value="">All Degrees</option>
                                {degreesData?.data?.degrees?.map((degree) => (
                                    <option key={degree._id} value={degree._id}>
                                        {degree.name}
                                    </option>
                                ))}
                            </select>
                            <select
                                value={filters.isActive}
                                onChange={(e) => handleFilterChange('isActive', e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
                            >
                                <option value="">All Status</option>
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                            </select>
                            <button
                                onClick={() => setFilters({
                                    search: '',
                                    degree: '',
                                    isActive: '',
                                    page: 1,
                                    limit: 10,
                                    sort: 'name'
                                })}
                                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center justify-center"
                            >
                                <XMarkIcon className="w-4 h-4 mr-2" />
                                Clear Filters
                            </button>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-900">
                                Specializations ({pagination.total_count || 0})
                            </h2>
                        </div>

                        {specializations.length === 0 ? (
                            <div className="text-center py-12">
                                <FunnelIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500">No specializations found</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Name
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Degree
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Status
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Order
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {specializations.map((specialization) => (
                                            <tr key={specialization._id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {specialization.name}
                                                        </div>
                                                        {specialization.description && (
                                                            <div className="text-sm text-gray-500">
                                                                {specialization.description}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className="text-sm text-gray-900">
                                                        {specialization.degree?.name || 'N/A'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${specialization.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                        }`}>
                                                        {specialization.isActive ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {specialization.order}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                    <div className="flex space-x-2">
                                                        <button
                                                            onClick={() => handleEdit(specialization)}
                                                            className="text-indigo-600 hover:text-indigo-900"
                                                        >
                                                            <PencilIcon className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(specialization._id)}
                                                            className="text-red-600 hover:text-red-900"
                                                        >
                                                            <TrashIcon className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Pagination */}
                        {pagination.total_pages > 1 && (
                            <div className="px-6 py-4 border-t border-gray-200">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-4">
                                        <div className="text-sm text-gray-700">
                                            Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to {Math.min(pagination.current_page * pagination.per_page, pagination.total_count)} of {pagination.total_count} results
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <label className="text-sm text-gray-700">Per page:</label>
                                            <select
                                                value={filters.limit}
                                                onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                                                className="px-2 py-1 border border-gray-300 rounded text-sm"
                                            >
                                                <option value={5}>5</option>
                                                <option value={10}>10</option>
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <button
                                            onClick={() => handleFilterChange('page', 1)}
                                            disabled={pagination.current_page === 1}
                                            className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                        >
                                            First
                                        </button>
                                        <button
                                            onClick={() => handleFilterChange('page', pagination.current_page - 1)}
                                            disabled={pagination.current_page === 1}
                                            className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                        >
                                            Previous
                                        </button>

                                        {/* Page Numbers */}
                                        <div className="flex space-x-1">
                                            {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                                                let pageNum;
                                                if (pagination.total_pages <= 5) {
                                                    pageNum = i + 1;
                                                } else if (pagination.current_page <= 3) {
                                                    pageNum = i + 1;
                                                } else if (pagination.current_page >= pagination.total_pages - 2) {
                                                    pageNum = pagination.total_pages - 4 + i;
                                                } else {
                                                    pageNum = pagination.current_page - 2 + i;
                                                }

                                                return (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => handleFilterChange('page', pageNum)}
                                                        className={`px-3 py-1 border rounded-md text-sm ${pageNum === pagination.current_page
                                                            ? 'bg-primary-600 text-white border-primary-600'
                                                            : 'border-gray-300 hover:bg-gray-50'
                                                            }`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        <button
                                            onClick={() => handleFilterChange('page', pagination.current_page + 1)}
                                            disabled={pagination.current_page === pagination.total_pages}
                                            className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                        >
                                            Next
                                        </button>
                                        <button
                                            onClick={() => handleFilterChange('page', pagination.total_pages)}
                                            disabled={pagination.current_page === pagination.total_pages}
                                            className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                        >
                                            Last
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal */}
            <AnimatePresence>
                {isModalOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                        onClick={handleCloseModal}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h3 className="text-lg font-semibold text-gray-900">
                                    {editingSpecialization ? 'Edit Specialization' : 'Add Specialization'}
                                </h3>
                            </div>
                            <form onSubmit={handleSubmit(onSubmit)} className="px-6 py-4 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Name *
                                    </label>
                                    <input
                                        {...register('name', { required: 'Name is required' })}
                                        className="input"
                                        placeholder="Enter specialization name"
                                    />
                                    {errors.name && (
                                        <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Description
                                    </label>
                                    <textarea
                                        {...register('description')}
                                        className="input"
                                        rows={3}
                                        placeholder="Enter description"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Degree *
                                    </label>
                                    <select
                                        {...register('degree', { required: 'Degree is required' })}
                                        className="input"
                                    >
                                        <option value="">Select Degree</option>
                                        {degreesData?.data?.degrees?.map((degree) => (
                                            <option key={degree._id} value={degree._id}>
                                                {degree.name}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.degree && (
                                        <p className="text-red-500 text-sm mt-1">{errors.degree.message}</p>
                                    )}
                                </div>


                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Order
                                    </label>
                                    <input
                                        {...register('order', { valueAsNumber: true })}
                                        type="number"
                                        className="input"
                                        placeholder="Enter order"
                                    />
                                </div>

                                <div className="flex items-center">
                                    <input
                                        {...register('isActive')}
                                        type="checkbox"
                                        className="h-4 w-4 text-accent-600 focus:ring-accent-500 border-gray-300 rounded"
                                    />
                                    <label className="ml-2 block text-sm text-gray-900">
                                        Active
                                    </label>
                                </div>

                                <div className="flex justify-end space-x-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={handleCloseModal}
                                        className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={createMutation.isLoading || updateMutation.isLoading}
                                        className="btn-primary"
                                    >
                                        {createMutation.isLoading || updateMutation.isLoading ? 'Saving...' : (editingSpecialization ? 'Update' : 'Create')}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default AdminSpecializations;
